"use client";

/**
 * poker-table.tsx — 灯下道场的老木桌
 *
 * 暖金木桌沿 + 深绿毡面 + 头顶一盏灯:桌子本身就是视觉中心。
 * 座位按行动环旋转:你永远坐在桌底,其余座位按行动顺序顺时针绕桌排开,
 * 场景切换时整圈弹性转动(reduce-motion 时瞬切);hero/villain + 行动线点名座位
 * 挂中文 pill 标签(封顶 3 枚),其余空座只留圆点(名字挂 title/aria)——
 * 你的座位发光 +「你」徽章。你的座位 = 翡翠 + 金圈;对手 = 暗红。
 */

import { motion, useReducedMotion } from "motion/react";
import { parseCards, parseBoardRanks, parseHand, handRelation, assignHandSuits, applyBoardTexture } from "@/lib/poker-cards";
import { flushStatusLabel } from "@/lib/flush-visual";
import type { FlushVisual, FlushStatus, Suit } from "@/lib/flush-visual";
import type { PokerSpot } from "@/lib/scene-types";
import { CardRow } from "@/components/playing-card";
import { positionZh, streetZh, potView, stacksView, bbInline, BB_UNIT_GLOSS_ENTRY, authoritativeGloss } from "@/lib/poker-terms-zh";
import { jargonZh } from "@/lib/jargon-zh";
import { ConceptBadge, renderGlossEntries } from "@/components/quiz-gloss";
import { DISPLAY_FONT } from "@/components/dojo";
import { cn } from "@/lib/cn";

type SeatId = "UTG" | "MP" | "CO" | "BTN" | "SB" | "BB";

/** 行动环序(顺时针):数据语义,永不改动。 */
const SEATS: SeatId[] = ["UTG", "MP", "CO", "BTN", "SB", "BB"];

/**
 * 屏幕槽位环:从「底」起顺时针(底 → 左下 → 左上 → 顶 → 右上 → 右下)。
 * 渲染时 hero 恒落槽 0(底),座位 i 取槽 (i - heroIdx + 6) % 6 ——
 * 于是行动顺序沿顺时针推进,你的下家出现在左手边,与真实牌桌方位一致。
 */
const SCREEN_SLOTS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 50, y: 88 }, // 底 — 你
  { x: 11, y: 62 }, // 左下
  { x: 17, y: 20 }, // 左上
  { x: 50, y: 9 }, // 顶
  { x: 83, y: 20 }, // 右上
  { x: 89, y: 62 }, // 右下
];

export function normalizePosition(raw: string | null): SeatId | null {
  if (!raw) return null;
  const s = raw.toUpperCase();
  if (s.includes("BTN") || s.includes("庄") || s.includes("BUTTON")) return "BTN";
  if (s.includes("SB") && !s.includes("BB")) return "SB";
  if (s.includes("BB") || s.includes("大盲") || s.includes("盲位")) return "BB";
  if (s.includes("UTG") || s.includes("前位") || s.includes("枪口")) return "UTG";
  if (s.includes("CO") || s.includes("庄前")) return "CO";
  if (s.includes("MP") || s.includes("HJ") || s.includes("LJ") || s.includes("中位") || s.includes("中间")) return "MP";
  if (s.includes("后位") || s.includes("LP") || s.includes("有利位置")) return "BTN";
  if (s.includes("EP") || s.includes("NP")) return "UTG";
  if (s.includes("IP") || s.includes("OOP")) return null;
  return null;
}

const SEAT_SPRING = { type: "spring", stiffness: 110, damping: 17 } as const;

function EstimatedTag() {
  return (
    <span
      title="数据未提供,按盲注基准估算"
      className="ml-1 font-sans text-[9px] font-medium tracking-normal text-stone-500"
    >
      估算
    </span>
  );
}

function PotStacksRow({ spot, compact }: { spot: PokerSpot; compact: boolean }) {
  const pot = potView(spot.pot, spot.street);
  const stacks = stacksView(spot.stacks);
  return (
    <div
      className={cn(
        "flex w-max flex-wrap items-center justify-center gap-x-3 gap-y-0.5 font-mono tabular-nums",
        compact ? "text-[10px]" : "text-[11px]",
      )}
    >
      <span className="whitespace-nowrap text-amber-200/85">
        底池{" "}
        <b
          className={cn(
            DISPLAY_FONT,
            "font-extrabold tracking-tight",
            compact ? "text-sm" : "text-base",
            pot.inferred && "text-amber-200/70",
          )}
        >
          {renderGlossEntries(bbInline(pot.text), [BB_UNIT_GLOSS_ENTRY])}
        </b>
        {pot.inferred && <EstimatedTag />}
      </span>
      <span className="whitespace-nowrap text-emerald-100/55">
        后手{" "}
        <b className={cn("font-semibold tracking-tight", stacks.inferred && "opacity-70")}>
          {renderGlossEntries(bbInline(stacks.text), [BB_UNIT_GLOSS_ENTRY])}
        </b>
        {stacks.inferred && <EstimatedTag />}
      </span>
    </div>
  );
}

function Seat({
  id,
  role,
  x,
  y,
  offset,
  compact,
  reduce,
  showLabel,
}: {
  id: SeatId;
  role: "hero" | "villain" | "empty";
  x: number;
  y: number;
  offset: number;
  compact?: boolean;
  reduce: boolean;
  /** F3 空座降密:false 且空座时不渲染文字 pill(名字挂 title/aria-label,悬停/读屏可得) */
  showLabel: boolean;
}) {
  const active = role !== "empty";
  const zh = positionZh(id);
  const hideLabel = role === "empty" && !showLabel;
  return (
    <motion.div
      className={cn("absolute flex flex-col items-center", compact ? "gap-0.5" : "gap-1", active && "z-10")}
      style={{ x: "-50%", y: "-50%" }}
      initial={reduce ? false : { opacity: 0, scale: 0.5, left: `${x}%`, top: `${y}%` }}
      animate={{ opacity: 1, scale: 1, left: `${x}%`, top: `${y}%` }}
      transition={
        reduce
          ? { duration: 0 }
          : {
              left: SEAT_SPRING,
              top: SEAT_SPRING,
              opacity: { duration: 0.36, delay: 0.04 * offset, ease: "easeOut" },
              scale: { type: "spring", stiffness: 300, damping: 24, delay: 0.04 * offset },
            }
      }
    >
      <span className="relative">
        {role === "hero" && !reduce && (
          <motion.span
            aria-hidden
            className="absolute -inset-1.5 rounded-full bg-emerald-300/25 blur-md"
            animate={{ opacity: [0.6, 0.2, 0.6], scale: [1, 1.3, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        <span
          title={hideLabel ? zh : undefined}
          aria-label={hideLabel ? zh : undefined}
          className={cn(
            "relative flex items-center justify-center rounded-full border-2 font-bold transition-transform duration-150 hover:scale-[1.08]",
            compact ? "size-7 text-[11px] sm:size-9 sm:text-[13px]" : "size-9 text-[13px] sm:size-11 sm:text-[15px]",
            role === "hero"
              ? "border-amber-200/90 bg-emerald-400 text-[#062015] shadow-[0_0_22px_rgba(52,211,153,0.55),0_0_46px_rgba(228,184,91,0.3)]"
              : role === "villain"
                ? "border-rose-300/40 bg-[#221013]/95 text-rose-200/90 shadow-[0_10px_24px_rgba(0,0,0,0.5)]"
                : "border-[#2C3B31] bg-[#0E150F]/90",
          )}
        >
          {role === "hero" ? (
            "你"
          ) : role === "villain" ? (
            "对"
          ) : (
            <span aria-hidden className={cn("rounded-full bg-[#39493D]", compact ? "size-1" : "size-1.5")} />
          )}
        </span>
      </span>
      {!hideLabel && (
        <span
          className={cn(
            "rounded-full px-2 py-px whitespace-nowrap",
            compact ? "text-[9px]" : "text-[10px]",
            role === "hero"
              ? "bg-emerald-400/15 font-bold text-emerald-100 ring-1 ring-emerald-300/50"
              : role === "villain"
                ? "bg-[#0B120E]/85 font-semibold text-rose-200/80 ring-1 ring-rose-300/25"
                : "bg-[#0B120E]/60 text-stone-500 ring-1 ring-white/5",
          )}
        >
          {role === "hero" ? `你 · ${zh}` : role === "villain" ? `对手 · ${zh}` : zh}
        </span>
      )}
    </motion.div>
  );
}

// compact(窄桌)状态条短词:n/5 计数 + 花色符号即 1 秒可读的教学核,长白话留给非 compact 全宽桌;
// 完整 label 仍是 flushStatusLabel 单一真值(persona 探针共用),此处仅显示层截短,不改真值源。
const COMPACT_FLUSH_WORD: Partial<Record<FlushStatus, string>> = {
  draw: "同花听牌",
  board_threat: "小心对手同花",
  sprout: "同花萌芽",
};

/** 同花状态条配色(方案丁·精简):已成金色,听牌淡蓝,威胁淡红,萌芽淡绿;not_yet 走中性条,不在此表。 */
const FLUSH_PILL_COLOR: Record<Exclude<FlushStatus, "none" | "not_yet">, string> = {
  made: "bg-amber-300/15 text-amber-100 ring-amber-300/55 shadow-[0_0_16px_rgba(252,211,77,0.18)]",
  draw: "bg-sky-400/15 text-sky-100 ring-sky-300/45",
  board_threat: "bg-rose-400/15 text-rose-100 ring-rose-300/45",
  sprout: "bg-emerald-400/15 text-emerald-100 ring-emerald-300/45",
};

/** 状态条内花色符号着色(与 PlayingCard 高亮淡彩同族)。 */
const FLUSH_SUIT_TEXT: Record<Suit, string> = {
  s: "text-sky-300",
  h: "text-rose-400",
  d: "text-orange-400",
  c: "text-emerald-400",
};

/**
 * #19d4 概念徽标就地可解:徽标(自造词如「同花萌芽」)点开 = 5 牌位图解卡。
 * 释义行取 poker-terms-zh 权威释义单一之家(禁新造词):
 *  - sprout(3/5 背门)=「后门」词条;draw(4/5)=「同花听牌」词条;
 *  - made/board_threat 全标签已自释,不硬凑释义行。
 */
const FLUSH_GLOSS_TERM: Partial<Record<Exclude<FlushStatus, "none" | "not_yet">, string>> = {
  sprout: "后门",
  draw: "同花听牌",
};

/**
 * 同花状态条(一枚,n/5 醒目):与牌面高亮共用 flushStatusLabel,所见即所验。
 * #19d4:状态条升级为 ConceptBadge——点开就地展开微型图解卡(5 牌位 + ≤3 行白话),
 * 零基础对「同花萌芽 3/5」不再无解。听牌(draw)微妙脉冲,暗示「差 1 张」。
 */
function FlushStatusPill({
  flush,
  compact,
  reduce,
}: {
  flush: FlushVisual;
  compact: boolean;
  reduce: boolean;
}) {
  if (!flush.label) return null;
  // T4 消歧条:无同花潜力(flop/turn)→ 中性灰一条「尚未成同花」,与标题「同花色手牌」互证;
  // 无 n/5、无花色符号,不抢四色状态条的视觉权重
  if (flush.status === "not_yet") {
    return (
      <span
        className={cn(
          "flex items-center whitespace-nowrap rounded-full bg-white/[0.06] font-semibold text-stone-400 ring-1 ring-white/10",
          compact ? "px-2 py-px text-[9px]" : "px-2.5 py-0.5 text-[10px]",
        )}
      >
        {flush.label}
      </span>
    );
  }
  if (flush.status === "none" || !flush.suit || !flush.count) return null;
  const m = flush.label.match(/^(.*?)\s*(\d)\/5（/);
  const fullWord = m && m[1] ? m[1] : flush.label;
  const word = compact ? (COMPACT_FLUSH_WORD[flush.status] ?? fullWord) : fullWord;
  const glossTerm = FLUSH_GLOSS_TERM[flush.status];
  return (
    <ConceptBadge
      word={word}
      count={flush.count}
      suit={flush.suit}
      suitTextCls={FLUSH_SUIT_TEXT[flush.suit]}
      pillCls={FLUSH_PILL_COLOR[flush.status]}
      compact={compact}
      pulse={flush.status === "draw" && !reduce}
      title={flush.label}
      gloss={glossTerm ? authoritativeGloss(glossTerm) : undefined}
    />
  );
}

export function PokerTable({ spot, compact = false }: { spot: PokerSpot; compact?: boolean }) {
  const reduce = useReducedMotion();
  const boardCards = applyBoardTexture(parseCards(spot.board) ?? parseBoardRanks(spot.board), spot.board_texture);
  const heroParsed = parseCards(spot.hero_hand);
  const heroCardsRaw = heroParsed ?? parseHand(spot.hero_hand);
  const heroRel = !heroParsed && heroCardsRaw ? handRelation(spot.hero_hand) : null;
  // P:rank-only 手牌按记法关系赋代表性花色,使「同花」在牌面可见(数据无具体花色,不臆造)
  const heroCards =
    heroCardsRaw && heroRel ? assignHandSuits(heroCardsRaw, heroRel) : heroCardsRaw;
  const heroSeat = normalizePosition(spot.hero_position);
  const villainSeat = normalizePosition(spot.villain_position);
  const heroIdx = heroSeat ? SEATS.indexOf(heroSeat) : -1;
  const reduced = reduce ?? false;
  // 同花方案丁:真实花色门控 + 河牌门控在 flushStatusLabel 内(rank-only/记法、河牌部分张数 → none)
  const flush = flushStatusLabel(spot.hero_hand, spot.board, spot.street);
  const highlightSuit: Suit | null = flush.status !== "none" && flush.suit ? flush.suit : null;
  // F3 空座降密:hero/villain + 行动线点名座位保留全名 pill(按 SEATS 序封顶 3 枚),
  // 其余空座只留圆点(位置名挂圆点 title/aria-label)——降视觉统治度,不删位置信息
  const labeled = (() => {
    const set = new Set<SeatId>();
    if (heroSeat) set.add(heroSeat);
    if (villainSeat) set.add(villainSeat);
    const actionText = jargonZh(spot.action_line);
    for (const id of SEATS) {
      if (set.size >= 3) break;
      if (set.has(id)) continue;
      // BTN 额外认「庄家位/按钮」:jargonZh 把「按钮」映射成「庄家位(BTN)」,只匹配「庄位」会漏
      if (
        actionText.includes(positionZh(id)) ||
        (id === "BTN" && (actionText.includes("庄家位") || actionText.includes("按钮")))
      ) {
        set.add(id);
      }
    }
    return set;
  })();

  return (
    // 矮视口(≤860px 高)且 ≥sm:桌面整体收窄 768→640(等比缩,几何零碰撞风险),为行动按钮让出垂直预算(r4 契约 item 17)
    <div className="relative mx-auto w-full max-w-3xl [@media(max-height:940px)]:sm:max-w-[40rem]">
      {/* 头顶一盏暖灯,光打在桌面上 */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 left-1/2 h-[130%] w-[120%] -translate-x-1/2 bg-[radial-gradient(ellipse_50%_42%_at_50%_0%,rgba(228,184,91,0.14),transparent_62%)]"
      />

      {/* 移动视口(<sm)桌体加高(4/3):compact 中央牌堆是绝对像素高,16/8 矮桌塞不下会撞「你」座(r2 契约 item 6);≥sm 维持原比例 */}
      <div className={cn("relative", compact ? "aspect-[4/3] sm:aspect-[16/8]" : "aspect-[16/10]")}>
        {/* 桌沿:老木金边 */}
        <div className="absolute inset-x-3 bottom-[8%] top-[8%] rounded-[50%] bg-[linear-gradient(180deg,#7A5C2B,#4E3A1A_60%,#33260F)] p-[7px] shadow-[0_28px_70px_-20px_rgba(0,0,0,0.85)]">
          {/* 毡面:灯光从上方斜打,四周收暗 */}
          <div className="size-full rounded-[50%] bg-[radial-gradient(ellipse_at_50%_28%,#2F7E5A,#1E5B3E_48%,#0F3524_80%)] shadow-[inset_0_26px_44px_-26px_rgba(228,184,91,0.35),inset_0_0_80px_rgba(0,0,0,0.5)]" />
        </div>

        {/* 座位:hero 恒落「底」槽,其余按行动环序顺时针排开;hero 不可解析时按原环序中性落座 */}
        {SEATS.map((id, i) => {
          const role =
            id === heroSeat ? "hero" : id === villainSeat ? "villain" : "empty";
          const offset = heroIdx >= 0 ? (i - heroIdx + SEATS.length) % SEATS.length : i;
          const slot = SCREEN_SLOTS[offset];
          return (
            <Seat
              key={id}
              id={id}
              role={role}
              x={slot.x}
              y={slot.y}
              offset={offset}
              compact={compact}
              reduce={reduced}
              showLabel={labeled.has(id)}
            />
          );
        })}

        {/* 牌面 + 底池:桌面正中(换局面时轻翻新) */}
        <motion.div
          key={`${spot.street}-${spot.board}-${spot.hero_hand}`}
          className={cn(
            "absolute left-1/2 flex flex-col items-center",
            compact ? "top-[38%] sm:top-[45%] gap-1.5" : "top-[45%] gap-2.5",
          )}
          style={{ x: "-50%", y: "-50%" }}
          initial={reduced ? false : { opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <span
            className={cn(
              "rounded-full bg-black/35 font-semibold tracking-[0.24em] text-emerald-100/75 ring-1 ring-white/10",
              compact ? "px-2.5 py-px text-[9px]" : "px-3 py-0.5 text-[10px]",
            )}
          >
            {streetZh(spot.street)}
          </span>
          <FlushStatusPill flush={flush} compact={compact} reduce={reduced} />
          <CardRow
            cards={boardCards}
            raw={spot.board}
            size={compact ? "md" : "lg"}
            highlightSuit={highlightSuit}
          />
          {/* B:底池/后手永远有数可答——数据缺 → 盲注基准估算并显式标「估算」 */}
          <PotStacksRow spot={spot} compact={compact} />
        </motion.div>
      </div>

      {/* 你的手牌:桌下聚光区,决策在这里发生 */}
      <div className={cn("relative flex flex-col items-center", compact ? "mt-3 gap-1.5 [@media(max-height:940px)]:mt-2" : "mt-5 gap-2")}>
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute left-1/2 -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(228,184,91,0.10),transparent_70%)]",
            compact ? "-top-5 h-20 w-2/3" : "-top-7 h-28 w-[72%]",
          )}
        />
        {(heroCards || spot.hero_hand) && (
          <div
            className={cn(
              "flex items-center",
              compact ? "flex-wrap justify-center gap-x-2.5 gap-y-1" : "flex-col gap-2",
            )}
          >
            <span
              className={cn(
                "font-semibold text-stone-500",
                compact ? "text-[9px] tracking-[0.2em]" : "text-[10px] tracking-[0.24em]",
              )}
            >
              你的手牌
            </span>
            <motion.span
              key={spot.hero_hand}
              className={cn("flex items-center", compact ? "gap-1.5" : "gap-2")}
              initial={reduced ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              <CardRow
                cards={heroCards}
                raw={spot.hero_hand}
                size={compact ? "md" : "lg"}
                highlightSuit={highlightSuit}
              />
              {heroRel && (
                <span
                  className={cn(
                    "rounded-md bg-[#E4DCC4] font-semibold text-[#7A6430]",
                    compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]",
                  )}
                >
                  {heroRel}
                </span>
              )}
            </motion.span>
            {/* 位置不可解析(OOP/IP 等)时不伪造座位,用聚光手牌区标明视角 */}
            {!heroSeat && (
              <span
                className={cn(
                  "rounded-full bg-emerald-400/15 font-semibold text-emerald-300 ring-1 ring-emerald-300/40",
                  compact ? "px-1.5 py-px text-[9px]" : "px-2 py-px text-[10px]",
                )}
              >
                你的视角
              </span>
            )}
          </div>
        )}
        <span
          className={cn(
            "flex items-center rounded-full bg-[#261E0D]/90 font-semibold text-amber-200 ring-1 ring-amber-300/40",
            compact ? "gap-1 px-2.5 py-0.5 text-[10px]" : "mt-1 gap-1.5 px-3 py-1 text-[11px]",
          )}
        >
          <motion.span
            aria-hidden
            className={cn("rounded-full bg-amber-300", compact ? "size-1" : "size-1.5")}
            animate={reduce ? undefined : { opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
          轮到你决策
        </span>
      </div>
    </div>
  );
}

